/**
 * Top bar: hamburger (mobile) + breadcrumbs + ⌘K trigger + theme toggle + user.
 *
 * The command palette is opened via the ``useCommandPalette`` hook so
 * other surfaces (e.g. an empty state) can also trigger it.
 */
import { Menu, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Breadcrumbs } from "./Breadcrumbs";
import { SidebarBrand, SidebarNav } from "./Sidebar";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { useState } from "react";
import { useCommandPalette } from "@/lib/command-palette-context";

export function Topbar() {
  const { open: openPalette } = useCommandPalette();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/75 sm:px-6">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <SidebarBrand />
          <SidebarNav onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="hidden md:block">
        <Breadcrumbs />
      </div>

      <div className="flex-1" />

      <Button
        variant="outline"
        size="sm"
        onClick={openPalette}
        className="hidden h-9 w-64 justify-start text-muted-foreground sm:inline-flex"
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="flex-1 text-left">Search…</span>
        <Kbd className="ml-2">⌘K</Kbd>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={openPalette}
        className="sm:hidden"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </Button>

      <ThemeToggle />
      <UserMenu />
    </header>
  );
}
